/**
 * IntegrationsAdmin — Health check de integraciones en tiempo real.
 *
 * Consume GET /api/dashboard/health y muestra el estado de:
 * Stripe, MercadoPago (pagos + suscripciones), Resend, DB, Brand, Clerk.
 *
 * Auto-refresh cada 30s. Requiere rol super_admin.
 */

import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import IntegrationSelfTest from "@/components/admin/IntegrationSelfTest";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { cn } from "@/lib/utils";
import type { HealthStatus } from "../../../api/dashboard/health";
import type { IncomeNotificationDiagnostics } from "../../../api/lib/incomeNotificationCronCore";
import {
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  Database,
  DollarSign,
  Globe,
  KeyRound,
  Loader2,
  Mail,
  RefreshCw,
  Send,
  ShieldCheck,
  Wallet,
  XCircle,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

// ─── Auto-refresh interval ────────────────────────────────────────────────────

const POLL_MS = 5_000;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Format cents as currency: -381 → "−$3.81", 10035 → "$100.35" (no currency suffix) */
function fmtCents(cents: number): string {
  const abs = Math.abs(cents / 100);
  const str = `$${abs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return cents < 0 ? `−${str}` : str;
}

// ─── Status badge helpers ─────────────────────────────────────────────────────

function StatusDot({ ok, warning }: { ok: boolean; warning?: boolean }) {
  if (ok) return <span className="h-2 w-2 rounded-full bg-emerald-500 shadow-[0_0_6px_2px_rgba(16,185,129,0.4)]" />;
  if (warning) return <span className="h-2 w-2 rounded-full bg-amber-400 shadow-[0_0_6px_2px_rgba(251,191,36,0.4)]" />;
  return <span className="h-2 w-2 rounded-full bg-red-500 shadow-[0_0_6px_2px_rgba(239,68,68,0.4)]" />;
}

function StatusBadge({ ok, warning, label }: { ok: boolean; warning?: boolean; label?: string }) {
  if (ok) return (
    <Badge className="gap-1 border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20">
      <CheckCircle2 className="h-3 w-3" />
      {label ?? "OK"}
    </Badge>
  );
  if (warning) return (
    <Badge className="gap-1 border-amber-400/30 bg-amber-400/10 text-amber-400 hover:bg-amber-400/20">
      <AlertTriangle className="h-3 w-3" />
      {label ?? "Warning"}
    </Badge>
  );
  return (
    <Badge className="gap-1 border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20">
      <XCircle className="h-3 w-3" />
      {label ?? "Error"}
    </Badge>
  );
}

// ─── Key-value row ────────────────────────────────────────────────────────────

function KVRow({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5 border-b border-zinc-800/60 last:border-0">
      <span className="text-xs text-zinc-500">{label}</span>
      <span className={cn("text-xs text-right", mono && "font-mono text-zinc-300")}>{value}</span>
    </div>
  );
}

// ─── Section card ─────────────────────────────────────────────────────────────

function IntegrationCard({
  icon: Icon,
  title,
  ok,
  warning,
  children,
  error,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  ok: boolean;
  warning?: boolean;
  children: React.ReactNode;
  error?: string;
}) {
  return (
    <Card
      className={cn(
        "border bg-zinc-900/60 transition-colors",
        ok ? "border-zinc-800" : warning ? "border-amber-500/20" : "border-red-500/20",
      )}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div
              className={cn(
                "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
                ok ? "bg-emerald-500/10" : warning ? "bg-amber-500/10" : "bg-red-500/10",
              )}
            >
              <Icon className={cn("h-4 w-4", ok ? "text-emerald-400" : warning ? "text-amber-400" : "text-red-400")} />
            </div>
            <div className="flex items-center gap-2">
              <CardTitle className="text-sm font-semibold text-zinc-100">{title}</CardTitle>
              <StatusDot ok={ok} warning={warning} />
            </div>
          </div>
          <StatusBadge ok={ok} warning={warning} />
        </div>
        {error && (
          <CardDescription className="mt-2 text-xs text-red-400 font-mono">
            ↳ {error}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="pt-0">{children}</CardContent>
    </Card>
  );
}

// ─── Overall status banner ────────────────────────────────────────────────────

function OverallBanner({ status, timestamp }: { status: HealthStatus["status"]; timestamp: string }) {
  const date = new Date(timestamp);
  const timeStr = date.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  if (status === "healthy") {
    return (
      <Alert className="border-emerald-500/30 bg-emerald-500/5">
        <CheckCircle2 className="h-4 w-4 text-emerald-400" />
        <AlertTitle className="text-emerald-400">Todas las integraciones funcionando</AlertTitle>
        <AlertDescription className="text-zinc-400 text-xs">
          Última verificación: {timeStr}
        </AlertDescription>
      </Alert>
    );
  }
  if (status === "degraded") {
    return (
      <Alert className="border-amber-400/30 bg-amber-400/5">
        <AlertTriangle className="h-4 w-4 text-amber-400" />
        <AlertTitle className="text-amber-400">Sistema degradado — algunos servicios con issues</AlertTitle>
        <AlertDescription className="text-zinc-400 text-xs">
          Última verificación: {timeStr}
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <Alert className="border-red-500/30 bg-red-500/5">
      <XCircle className="h-4 w-4 text-red-400" />
      <AlertTitle className="text-red-400">Error crítico — integraciones esenciales no disponibles</AlertTitle>
      <AlertDescription className="text-zinc-400 text-xs">
        Última verificación: {timeStr}
      </AlertDescription>
    </Alert>
  );
}

// ─── Income notifications (ganancias) card ────────────────────────────────────

// Reuses the canonical diagnostics type from the backend (single source of truth)
// and extends it with the extra fields the GET handler decorates the response with.
type IncomeNotificationsDiag = IncomeNotificationDiagnostics & {
  summary?: {
    healthy: boolean;
    has_configuration_issues: boolean;
    has_pending_income_emails: boolean;
    needs_migration_043: boolean;
  };
  hints?: {
    run_cron_manually: string;
    /** Railway in-process cron path (alias histórico: vercel_cron_path). */
    cron_path?: string;
    vercel_cron_path?: string;
  };
  has_issues?: boolean;
};

function IncomeNotificationsCard({
  getAuthHeaders,
}: {
  getAuthHeaders: () => Promise<Record<string, string>>;
}) {
  const [diag, setDiag] = useState<IncomeNotificationsDiag | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const getAuthRef = useRef(getAuthHeaders);
  getAuthRef.current = getAuthHeaders;

  const fetchDiag = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const headers = await getAuthRef.current();
      const res = await fetch("/api/admin/income-notifications", { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as IncomeNotificationsDiag;
      setDiag(json);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { void fetchDiag(); }, [fetchDiag]);
  useEffect(() => {
    const id = window.setInterval(() => void fetchDiag(true), 15_000);
    return () => window.clearInterval(id);
  }, [fetchDiag]);

  const runNow = useCallback(async () => {
    setRunning(true);
    setRunResult(null);
    try {
      const headers = await getAuthRef.current();
      const res = await fetch("/api/admin/income-notifications", { method: "POST", headers });
      const json = await res.json() as { ok?: boolean; processed?: { sent: number; failed: number; manual: number; stripe: number } };
      if (json.ok && json.processed) {
        setRunResult(`✓ Enviados: ${json.processed.sent}, fallidos: ${json.processed.failed} (manual ${json.processed.manual}, stripe ${json.processed.stripe})`);
      } else {
        setRunResult("✗ Falló la ejecución manual");
      }
      void fetchDiag(true);
    } catch (e) {
      setRunResult(`✗ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setRunning(false);
    }
  }, [fetchDiag]);

  if (loading && !diag) {
    return (
      <IntegrationCard icon={DollarSign} title="Webhook ganancias (Resend)" ok={false} warning>
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <Loader2 className="h-3 w-3 animate-spin" /> Cargando diagnóstico…
        </div>
      </IntegrationCard>
    );
  }

  if (err) {
    return (
      <IntegrationCard icon={DollarSign} title="Webhook ganancias (Resend)" ok={false}>
        <p className="text-sm text-red-400">Error: {err}</p>
      </IntegrationCard>
    );
  }

  if (!diag) return null;

  const healthy = diag.summary?.healthy === true;
  const hasPending = diag.pending_counts.total > 0;
  const hasIssues = diag.failures_hint.length > 0;

  return (
    <IntegrationCard
      icon={DollarSign}
      title="Webhook ganancias (Resend)"
      ok={healthy}
      warning={!healthy && !hasIssues}
    >
      <KVRow label="Resend listo" value={<StatusBadge ok={diag.resend_readiness.can_send} />} />
      <KVRow
        label="Destinatarios"
        value={
          diag.recipient_count > 0
            ? <StatusBadge ok label={`${diag.recipient_count}`} />
            : <StatusBadge ok={false} label="Ninguno" />
        }
      />
      <KVRow
        label="Migración 043"
        value={
          diag.migration.manual_transactions_income_column || diag.migration.stripe_transactions_income_column
            ? <StatusBadge ok />
            : <StatusBadge ok={false} label="Falta" />
        }
      />

      <div className="mt-3 rounded border border-zinc-800 bg-zinc-900/50 p-2">
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Pendientes (sin email)</p>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <span className="text-zinc-500">MP recientes ({diag.windows.recent_minutes}min):</span>
          <span className="text-right tabular-nums text-zinc-200">{diag.pending_counts.manual_recent}</span>
          <span className="text-zinc-500">MP backlog ({diag.windows.backlog_hours}h):</span>
          <span className="text-right tabular-nums text-zinc-200">{diag.pending_counts.manual_backlog}</span>
          <span className="text-zinc-500">Stripe recientes:</span>
          <span className="text-right tabular-nums text-zinc-200">{diag.pending_counts.stripe_recent}</span>
          <span className="text-zinc-500">Stripe backlog:</span>
          <span className="text-right tabular-nums text-zinc-200">{diag.pending_counts.stripe_backlog}</span>
          <span className="border-t border-zinc-800 pt-1 font-semibold text-zinc-400">Total:</span>
          <span className={cn("border-t border-zinc-800 pt-1 text-right tabular-nums font-semibold",
            hasPending ? "text-amber-400" : "text-emerald-400")}>
            {diag.pending_counts.total}
          </span>
        </div>
      </div>

      {hasIssues && (
        <div className="mt-2 rounded border border-amber-500/30 bg-amber-500/5 p-2">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-amber-400">Problemas</p>
          <ul className="space-y-0.5 text-[11px] text-amber-300">
            {diag.failures_hint.map((h, i) => <li key={i}>• {h}</li>)}
          </ul>
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        <Button
          onClick={() => void runNow()}
          disabled={running || !diag.resend_readiness.can_send || diag.recipient_count === 0}
          variant="outline"
          size="sm"
          className="h-7 text-xs"
        >
          {running ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Send className="mr-1 h-3 w-3" />}
          Enviar pendientes ahora
        </Button>
        {runResult && <span className="text-[10px] text-zinc-400">{runResult}</span>}
      </div>

      <p className="mt-2 text-[10px] text-zinc-600">
        Cron: <code className="font-mono">/api/cron/process-pending-income-notifications</code> · cada 15min
      </p>
    </IntegrationCard>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

// Build a deterministic signature of the things we want to flash on when changed
function buildHealthSignature(h: HealthStatus): string {
  return JSON.stringify({
    status: h.status,
    db: h.database.connected,
    stripe_ok: h.stripe.api_reachable,
    stripe_keys: h.stripe.webhook_secrets_count,
    mp_pay_ok: h.mercadopago.payments.api_reachable,
    mp_pay_token: h.mercadopago.payments.access_token_set,
    mp_pay_secret: h.mercadopago.payments.webhook_secret_set,
    mp_sub_token: h.mercadopago.subscriptions.access_token_set,
    mp_sub_secret: h.mercadopago.subscriptions.webhook_secret_set,
    resend_ok: h.resend.api_reachable,
    resend_key: h.resend.api_key_set,
    missing: h.missing_vars.length,
  });
}

// ─── Stripe Cleanup Duplicates Button ───────────────────────────────────────
/**
 * Botón super_admin para limpiar Stripe products duplicados.
 *
 * Flow:
 *   1. Click "Run dry-run" → llama GET /api/admin/stripe-cleanup-duplicates
 *      → muestra reporte con candidatos + summary (NO toca Stripe)
 *   2. Si el reporte se ve bien → "Apply (archive)" → POST con ?apply=true
 *      → archiva en batches de 200 max (pasa max=N para tunear)
 *
 * Archive ≠ delete: setea active=false, reversible. Skip products con
 * suscripciones activas + canonicals en membership_products. Conservador.
 */
function StripeCleanupButton() {
  const { getAuthHeaders } = useAdminAuth();
  const [stage, setStage] = useState<"idle" | "dry-running" | "dry-done" | "applying" | "applied">("idle");
  const [report, setReport] = useState<unknown | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const runDryRun = async () => {
    setStage("dry-running");
    setErrMsg(null);
    try {
      const headers = await getAuthHeaders();
      const r = await fetch("/api/admin/stripe-cleanup-duplicates", { method: "GET", headers });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || data.message || `HTTP ${r.status}`);
      setReport(data);
      setStage("dry-done");
    } catch (err) {
      setErrMsg(err instanceof Error ? err.message : "Error");
      setStage("idle");
    }
  };

  const runApply = async () => {
    if (!confirm("¿Archivar los Stripe products duplicados? Reversible (active=false), pero confirmá.")) return;
    setStage("applying");
    setErrMsg(null);
    try {
      const headers = await getAuthHeaders();
      const r = await fetch("/api/admin/stripe-cleanup-duplicates?apply=true&max=200", {
        method: "POST",
        headers,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || data.message || `HTTP ${r.status}`);
      setReport(data);
      setStage("applied");
    } catch (err) {
      setErrMsg(err instanceof Error ? err.message : "Error");
      setStage("dry-done");
    }
  };

  const summary = (report as { summary?: Record<string, number> } | null)?.summary;
  const candidatesPreview = (report as { candidates?: Array<{ id: string; name: string; created_iso: string }> } | null)?.candidates ?? [];

  return (
    <div className="mt-3 rounded-md border border-zinc-800 bg-zinc-900/40 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-xs font-semibold text-zinc-300">Cleanup duplicados Stripe</div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={runDryRun}
            disabled={stage === "dry-running" || stage === "applying"}
            className="rounded-md border border-zinc-700 bg-zinc-800/60 px-2.5 py-1 text-[11px] font-semibold text-zinc-200 hover:bg-zinc-700 disabled:opacity-50"
          >
            {stage === "dry-running" ? "Auditando..." : "Dry-run"}
          </button>
          {stage === "dry-done" && summary && (summary.candidates_count ?? 0) > 0 && (
            <button
              type="button"
              onClick={runApply}
              disabled={(stage as string) === "applying"}
              className="rounded-md border border-amber-600/40 bg-amber-500/10 px-2.5 py-1 text-[11px] font-semibold text-amber-300 hover:bg-amber-500/20 disabled:opacity-50"
            >
              {(stage as string) === "applying" ? "Archivando..." : `Archive (${summary.candidates_count})`}
            </button>
          )}
        </div>
      </div>

      {errMsg && (
        <p className="mb-2 rounded-md bg-red-500/10 border border-red-500/30 px-2 py-1 text-[11px] text-red-300">
          {errMsg}
        </p>
      )}

      {summary && (
        <div className="space-y-1 text-[11px] text-zinc-400">
          <div>Total products activos: <span className="text-zinc-200">{summary.total_active_products}</span></div>
          <div>Con lookup_key (mantener): <span className="text-emerald-400">{summary.skipped_with_lookup_key}</span></div>
          <div>Con suscripción activa: <span className="text-emerald-400">{summary.skipped_with_active_sub_or_canonical}</span></div>
          <div>Nombre fuera de filtro: <span className="text-zinc-500">{summary.skipped_name_not_matching}</span></div>
          <div>Ya archivados: <span className="text-zinc-500">{summary.skipped_already_archived}</span></div>
          <div className="font-semibold">
            ★ Candidatos a archivar:{" "}
            <span className="text-amber-300">{summary.candidates_count}</span>
          </div>
          {(stage as string) === "applied" && (
            <div className="mt-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-emerald-300">
              ✓ {summary.archived_products ?? 0} products + {summary.archived_prices ?? 0} prices archivados
              {(summary.errors_count ?? 0) > 0 && ` · ${summary.errors_count} errores`}
            </div>
          )}
        </div>
      )}

      {(stage === "dry-done" || (stage as string) === "applied") && candidatesPreview.length > 0 && (
        <details className="mt-2 text-[10px] text-zinc-500">
          <summary className="cursor-pointer hover:text-zinc-300">Ver primeros {Math.min(candidatesPreview.length, 100)} candidatos</summary>
          <ul className="mt-1 max-h-40 overflow-y-auto space-y-0.5 font-mono">
            {candidatesPreview.slice(0, 100).map((c) => (
              <li key={c.id}>{c.id} · {c.name} · {c.created_iso}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

export default function IntegrationsAdmin() {
  const { getAuthHeaders } = useAdminAuth();
  const [data, setData] = useState<HealthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [secsSince, setSecsSince] = useState(0);
  const [changesDetected, setChangesDetected] = useState(0);
  const [flashUntil, setFlashUntil] = useState<number>(0);
  const prevSignatureRef = useRef<string | null>(null);
  const getAuthHeadersRef = useRef(getAuthHeaders);
  getAuthHeadersRef.current = getAuthHeaders;

  const fetchHealth = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const headers = await getAuthHeadersRef.current();
      const res = await fetch("/api/dashboard/health", { headers });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`HTTP ${res.status}: ${res.statusText}${body ? ` — ${body.slice(0, 200)}` : ""}`);
      }
      const json = (await res.json()) as HealthStatus;
      // Change detection — flash UI if any monitored value flipped
      const sig = buildHealthSignature(json);
      if (prevSignatureRef.current !== null && prevSignatureRef.current !== sig) {
        setChangesDetected((n) => n + 1);
        setFlashUntil(Date.now() + 2500); // flash for 2.5s
      }
      prevSignatureRef.current = sig;
      setData(json);
      setLastRefresh(new Date());
      if (silent) setError(null);
    } catch (err) {
      if (!silent) setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { void fetchHealth(); }, [fetchHealth]);

  useEffect(() => {
    const id = window.setInterval(() => { void fetchHealth({ silent: true }); }, POLL_MS);
    return () => window.clearInterval(id);
  }, [fetchHealth]);

  // Tick counter — resets to 0 whenever lastRefresh changes
  useEffect(() => {
    setSecsSince(0);
    const id = window.setInterval(() => setSecsSince((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [lastRefresh]);

  // Force re-render when flash window expires
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (flashUntil <= Date.now()) return;
    const id = window.setTimeout(() => forceTick((n) => n + 1), flashUntil - Date.now());
    return () => window.clearTimeout(id);
  }, [flashUntil]);

  const d = data;
  const isFlashing = Date.now() < flashUntil;

  return (
    <div className={cn("mx-auto max-w-7xl space-y-8 transition-colors duration-500", isFlashing && "ring-2 ring-amber-400/40 rounded-lg")}>
      <AdminPageHeader
        description="Health check en vivo: Stripe, MP, DB, Resend, Clerk… Gaps de producto → Config Health."
        actions={
          <div className="flex items-center gap-2">
            <div className="hidden items-center gap-2 text-xs text-zinc-500 sm:flex">
              <span className="relative flex h-2 w-2">
                <span className={cn("absolute inline-flex h-full w-full animate-ping rounded-full opacity-75", isFlashing ? "bg-amber-400" : "bg-emerald-400")} />
                <span className={cn("relative inline-flex h-2 w-2 rounded-full", isFlashing ? "bg-amber-500" : "bg-emerald-500")} />
              </span>
              <span>
                En vivo · {POLL_MS / 1000}s
                {lastRefresh && <> · verificado hace <strong className="tabular-nums">{secsSince}s</strong></>}
                {changesDetected > 0 && <> · <span className="text-amber-500">{changesDetected} cambio{changesDetected === 1 ? "" : "s"}</span></>}
              </span>
            </div>
            <Button
              onClick={() => void fetchHealth()}
              disabled={loading}
              variant="outline"
              size="sm"
              className="border-zinc-700 bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-zinc-100"
            >
              <RefreshCw className={cn("mr-2 h-4 w-4", loading && "animate-spin")} />
              Actualizar
            </Button>
          </div>
        }
      />

      {/* Error fetching */}
      {error && (
        <Alert variant="destructive" className="border-red-500/30 bg-red-500/5">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Error al cargar</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Loading skeletons */}
      {loading && !d && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-52 w-full rounded-xl bg-zinc-800/60" />
          ))}
        </div>
      )}

      {/* Content */}
      {d && (
        <div className="space-y-6">
          {/* Overall banner */}
          <OverallBanner status={d.status} timestamp={d.timestamp} />

          {/* Self-test — verifies every claim about the integration in
              real time (DB tables, columns, KV backend, env vars, recent
              tracking activity). This is the single source of truth for
              "is this actually working right now?". */}
          <IntegrationSelfTest />

          {/* Routing policy panel — explicit per-country provider mapping */}
          <Card className="border-zinc-800 bg-zinc-900/50">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Globe className="h-4 w-4 text-zinc-400" />
                Política de routing por país
              </CardTitle>
              <CardDescription className="text-xs">
                Cada país usa exactamente un proveedor. El backend rechaza solicitudes que no respetan esta política.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3">
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-base">🇦🇷</span>
                  <span className="text-sm font-semibold text-emerald-300">Argentina (AR)</span>
                </div>
                <p className="text-xs text-zinc-400">
                  → <strong className="text-zinc-200">MercadoPago</strong> (ARS)
                </p>
                <p className="mt-1 text-[11px] text-zinc-500">
                  Endpoint: <code className="font-mono">/api/mercadopago/create-subscription</code>
                </p>
                <p className="mt-0.5 text-[11px]">
                  <StatusDot ok={d.mercadopago.payments.api_reachable && d.mercadopago.payments.access_token_set} />
                  <span className="ml-1.5 text-zinc-400">
                    {d.mercadopago.payments.api_reachable && d.mercadopago.payments.access_token_set
                      ? "Configurado y alcanzable"
                      : "Faltan credenciales o API caída"}
                  </span>
                </p>
              </div>
              <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-3">
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-base">🌎</span>
                  <span className="text-sm font-semibold text-blue-300">Internacional (no-AR)</span>
                </div>
                <p className="text-xs text-zinc-400">
                  → <strong className="text-zinc-200">Stripe</strong> (USD)
                </p>
                <p className="mt-1 text-[11px] text-zinc-500">
                  Endpoint: <code className="font-mono">/api/create-subscription-checkout</code>
                </p>
                <p className="mt-0.5 text-[11px]">
                  <StatusDot ok={d.stripe.api_reachable && d.stripe.api_key_set} />
                  <span className="ml-1.5 text-zinc-400">
                    {d.stripe.api_reachable && d.stripe.api_key_set
                      ? `Configurado · modo ${d.stripe.mode}`
                      : "Faltan credenciales o API caída"}
                  </span>
                </p>
              </div>
            </CardContent>
            <div className="border-t border-zinc-800 px-6 py-2">
              <p className="text-[10px] text-zinc-500">
                <strong className="text-zinc-400">Defensa:</strong> Stripe rechaza{" "}
                <code className="font-mono">currency=ars</code> o <code className="font-mono">country=AR</code>;
                MercadoPago rechaza <code className="font-mono">currency≠ARS</code>.
              </p>
            </div>
          </Card>

          {/* Missing vars panel */}
          {d.missing_vars.length > 0 && (() => {
            const criticalVars = d.missing_vars.filter((v) => v.critical);
            const nonCriticalVars = d.missing_vars.filter((v) => !v.critical);
            return (
              <Card className="border-amber-500/30 bg-zinc-900/60">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
                      <KeyRound className="h-4 w-4 text-amber-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-sm font-semibold text-zinc-100">Variables faltantes</CardTitle>
                        {/* Live pulse */}
                        <span className="relative flex h-2 w-2 shrink-0">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-400" />
                        </span>
                      </div>
                      <CardDescription className="text-xs text-zinc-500 mt-0.5">
                        {d.missing_vars.length} variable{d.missing_vars.length !== 1 ? "s" : ""} sin configurar
                        {" · "}
                        <span className="tabular-nums">verificado hace {secsSince}s</span>
                        {" · "}
                        <span>actualiza cada {POLL_MS / 1000}s</span>
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 space-y-4">
                  {criticalVars.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-red-500">Críticas</p>
                      {criticalVars.map((v) => (
                        <div key={v.key} className="flex items-start justify-between gap-3 rounded-md border border-red-500/20 bg-red-500/5 px-3 py-2">
                          <div className="min-w-0">
                            <code className="text-xs font-mono text-red-300">{v.key}</code>
                            <p className="mt-0.5 text-xs text-zinc-500">{v.description}</p>
                          </div>
                          <Badge className="shrink-0 border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 text-[10px]">
                            Crítico
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                  {nonCriticalVars.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-500">Recomendadas</p>
                      {nonCriticalVars.map((v) => (
                        <div key={v.key} className="flex items-start justify-between gap-3 rounded-md border border-amber-500/20 bg-amber-500/5 px-3 py-2">
                          <div className="min-w-0">
                            <code className="text-xs font-mono text-amber-300">{v.key}</code>
                            <p className="mt-0.5 text-xs text-zinc-500">{v.description}</p>
                          </div>
                          <Badge className="shrink-0 border-amber-400/30 bg-amber-400/10 text-amber-400 hover:bg-amber-400/20 text-[10px]">
                            Recomendado
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="pt-1">
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="border-zinc-700 bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-zinc-100"
                    >
                      <a href="https://railway.com/dashboard" target="_blank" rel="noopener noreferrer">
                        Configurar en Railway →
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Top stats row */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">

            {/* Stripe txns */}
            <Card className="border-zinc-800 bg-zinc-900/60">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Stripe · 24h</p>
                    <p className="mt-1.5 text-3xl font-bold tabular-nums leading-none text-zinc-100">
                      {d.recent_transactions.stripe_24h.toLocaleString("es-AR")}
                    </p>
                    <p className="mt-1 text-xs text-zinc-600">transacciones</p>
                  </div>
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-500/10">
                    <CreditCard className="h-4 w-4 text-violet-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* MP txns */}
            <Card className="border-zinc-800 bg-zinc-900/60">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">MercadoPago · 24h</p>
                    <p className="mt-1.5 text-3xl font-bold tabular-nums leading-none text-zinc-100">
                      {d.recent_transactions.mercadopago_24h.toLocaleString("es-AR")}
                    </p>
                    <p className="mt-1 text-xs text-zinc-600">transacciones</p>
                  </div>
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-500/10">
                    <Wallet className="h-4 w-4 text-blue-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stripe balance */}
            {d.stripe.balance_usd ? (
              <Card className={cn(
                "border bg-zinc-900/60",
                d.stripe.balance_usd.available < 0 ? "border-red-500/20" : "border-zinc-800"
              )}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Balance Stripe</p>
                      <p className={cn(
                        "mt-1.5 text-2xl font-bold tabular-nums leading-none",
                        d.stripe.balance_usd.available < 0 ? "text-red-400" : "text-zinc-100"
                      )}>
                        {fmtCents(d.stripe.balance_usd.available)}
                        <span className="ml-1 text-xs font-normal text-zinc-600">USD</span>
                      </p>
                      <p className="mt-1 text-xs text-zinc-600">
                        pendiente{" "}
                        <span className={cn(
                          "font-mono",
                          d.stripe.balance_usd.pending > 0 ? "text-amber-400" : "text-zinc-500"
                        )}>
                          {fmtCents(d.stripe.balance_usd.pending)}
                        </span>
                      </p>
                    </div>
                    <div className={cn(
                      "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                      d.stripe.balance_usd.available < 0 ? "bg-red-500/10" : "bg-emerald-500/10"
                    )}>
                      <Wallet className={cn(
                        "h-4 w-4",
                        d.stripe.balance_usd.available < 0 ? "text-red-400" : "text-emerald-400"
                      )} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-zinc-800 bg-zinc-900/60">
                <CardContent className="p-4 flex items-center justify-center h-full text-xs text-zinc-600">
                  Balance no disponible
                </CardContent>
              </Card>
            )}

            {/* Brand */}
            <Card className="border-zinc-800 bg-zinc-900/60">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Brand</p>
                    <p className="mt-1.5 text-base font-bold leading-tight text-zinc-100 truncate">
                      {d.brand.source_key ?? <span className="text-red-400">No config</span>}
                    </p>
                    <p className="mt-1 text-xs text-zinc-600 truncate">{d.brand.site ?? "—"}</p>
                  </div>
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-zinc-700/50">
                    <Globe className="h-4 w-4 text-zinc-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

          </div>

          {/* Integration cards grid */}
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">

            {/* ── Database ── */}
            <IntegrationCard
              icon={Database}
              title="Base de datos"
              ok={d.database.connected}
              error={d.database.error}
            >
              <KVRow label="Conectada" value={<StatusBadge ok={d.database.connected} />} />
              {Object.entries(d.database.tables).map(([table, count]) => (
                <KVRow
                  key={table}
                  label={table}
                  value={
                    count === -1
                      ? <span className="text-red-400">tabla no existe</span>
                      : <span className="font-mono text-zinc-300">{count.toLocaleString("es-AR")}</span>
                  }
                  mono
                />
              ))}
            </IntegrationCard>

            {/* ── Stripe ──
             *  Misma regla que MP Pagos: el webhook_secret cuenta para el OK.
             *  Sin webhook_secret Stripe igual procesa el pago, pero los eventos
             *  (checkout.session.completed, invoice.paid, etc.) no se verifican
             *  → el backend rechaza el webhook → user_memberships no se activa.
             */}
            <IntegrationCard
              icon={CreditCard}
              title="Stripe"
              ok={
                d.stripe.api_reachable &&
                d.stripe.api_key_set &&
                d.stripe.webhook_secrets_count > 0
              }
              warning={
                d.stripe.api_key_set &&
                (
                  !d.stripe.api_reachable ||
                  d.stripe.webhook_secrets_count === 0
                )
              }
              error={d.stripe.error}
            >
              <KVRow label="API key configurada" value={<StatusBadge ok={d.stripe.api_key_set} />} />
              <KVRow label="API alcanzable" value={<StatusBadge ok={d.stripe.api_reachable} warning={!d.stripe.api_reachable} />} />
              <KVRow
                label="Modo"
                value={
                  d.stripe.mode === "test"
                    ? <Badge className="border-amber-400/30 bg-amber-400/10 text-amber-400 hover:bg-amber-400/20">Test / Sandbox</Badge>
                    : d.stripe.mode === "live"
                    ? <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20">Live / Producción</Badge>
                    : <Badge className="border-zinc-600/30 bg-zinc-700/20 text-zinc-500 hover:bg-zinc-700/30">Desconocido</Badge>
                }
              />
              <KVRow
                label="Webhook secrets"
                value={
                  <StatusBadge
                    ok={d.stripe.webhook_secrets_count > 0}
                    warning={false}
                    label={`${d.stripe.webhook_secrets_count} configurado${d.stripe.webhook_secrets_count !== 1 ? "s" : ""}`}
                  />
                }
              />
              {d.stripe.balance_usd && (
                <>
                  <KVRow
                    label="Balance disponible"
                    value={
                      <span className={cn("font-mono", d.stripe.balance_usd.available < 0 ? "text-red-400" : "text-zinc-300")}>
                        {fmtCents(d.stripe.balance_usd.available)}
                      </span>
                    }
                    mono
                  />
                  <KVRow
                    label="Balance pendiente"
                    value={<span className="font-mono text-zinc-400">{fmtCents(d.stripe.balance_usd.pending)}</span>}
                    mono
                  />
                </>
              )}

              {/* Cleanup de duplicados — solo super_admin. Los pre-fix products
                  sin lookup_key se acumularon (100+ "Membresía Standard/VIP")
                  por el bug de price_data inline. Este botón ejecuta el endpoint
                  /api/admin/stripe-cleanup-duplicates con dry-run primero. */}
              <StripeCleanupButton />
            </IntegrationCard>

            {/* ── MercadoPago Pagos ──
             *  Reglas para que la card "diga lo real":
             *  - OK ⇒ TODO listo: token + API + webhook_secret. Sin webhook_secret
             *    los pagos cobran pero el backend rechaza la notificación con HTTP 500
             *    → user_memberships NO se activa, /muchas-gracias queda en pending.
             *  - WARNING ⇒ procesa pagos pero webhooks fallan o API no alcanzable.
             *  - ERROR ⇒ falta access_token o el endpoint MP devolvió error.
             */}
            <IntegrationCard
              icon={Wallet}
              title="MercadoPago — Pagos"
              ok={
                d.mercadopago.payments.api_reachable &&
                d.mercadopago.payments.access_token_set &&
                d.mercadopago.payments.webhook_secret_set
              }
              warning={
                d.mercadopago.payments.access_token_set &&
                (
                  !d.mercadopago.payments.api_reachable ||
                  !d.mercadopago.payments.webhook_secret_set
                )
              }
              error={d.mercadopago.payments.error}
            >
              <KVRow label="Access token" value={<StatusBadge ok={d.mercadopago.payments.access_token_set} />} />
              <KVRow label="API alcanzable" value={<StatusBadge ok={d.mercadopago.payments.api_reachable} warning={!d.mercadopago.payments.api_reachable} />} />
              <KVRow
                label="Webhook secret"
                value={
                  d.mercadopago.payments.webhook_secret_set
                    ? <StatusBadge ok />
                    : <span className="flex items-center gap-1"><StatusBadge ok={false} warning label="CRÍTICO" /><code className="ml-1 text-[10px] text-zinc-600 font-mono">MERCADOPAGO_WEBHOOK_SECRET</code></span>
                }
              />
              {!d.mercadopago.payments.webhook_secret_set && (
                <p className="mt-2 rounded-md bg-red-500/5 border border-red-500/20 px-2.5 py-2 text-xs text-red-400">
                  ⚠ Sin <code className="font-mono">MERCADOPAGO_WEBHOOK_SECRET</code> los pagos cobran pero los webhooks de MP devuelven HTTP 500 en producción → <code className="font-mono">user_memberships</code> no se activa, el usuario queda en <code className="font-mono">/muchas-gracias</code> con status pending. Copiar el secret desde MP Dashboard → Webhooks → Tu URL → "Secret".
                </p>
              )}
              <KVRow
                label="Public key"
                value={
                  d.mercadopago.payments.public_key_set
                    ? <StatusBadge ok />
                    : <span className="flex items-center gap-1"><StatusBadge ok={false} /><code className="ml-1 text-[10px] text-zinc-600 font-mono">VITE_MERCADOPAGO_PUBLIC_KEY</code></span>
                }
              />
              <KVRow label="Checkout Basic AR" value={<StatusBadge ok={d.mercadopago.payments.checkout_basic_ar} />} />
              <KVRow label="Checkout VIP AR" value={<StatusBadge ok={d.mercadopago.payments.checkout_vip_ar} />} />
            </IntegrationCard>

            {/* ── MercadoPago Suscripciones ── */}
            {/*
             * Política del proyecto: para suscripciones AR usamos direct
             * checkout links pre-generados en env (VITE_MP_CHECKOUT_BASIC_AR
             * y VITE_MP_CHECKOUT_VIP_AR). Esto es el fallback INTENCIONAL para
             * no depender de un Access Token de suscripciones por API.
             *
             * Por eso la card ahora considera "OK" si:
             *   1) MERCADOPAGO_SUBSCRIPTION_ACCESS_TOKEN está configurado, O
             *   2) los direct links están configurados (fallback intencional), O
             *   3) usa MERCADOPAGO_ACCESS_TOKEN como fallback.
             */}
            <IntegrationCard
              icon={Zap}
              title="MercadoPago — Suscripciones"
              ok={
                d.mercadopago.subscriptions.access_token_set ||
                d.mercadopago.subscriptions.direct_checkout_links_configured ||
                d.mercadopago.subscriptions.using_payments_token_as_fallback
              }
              warning={
                // Solo warning si NO hay ni access token propio NI direct links
                // (estamos cayendo al token de pagos como último recurso).
                d.mercadopago.subscriptions.using_payments_token_as_fallback &&
                !d.mercadopago.subscriptions.access_token_set &&
                !d.mercadopago.subscriptions.direct_checkout_links_configured
              }
            >
              <KVRow
                label="Access token propio"
                value={
                  d.mercadopago.subscriptions.access_token_set
                    ? <StatusBadge ok />
                    : d.mercadopago.subscriptions.direct_checkout_links_configured
                      ? <span className="flex items-center gap-1"><StatusBadge ok label="Opcional" /><code className="ml-1 text-[10px] text-zinc-600 font-mono">MERCADOPAGO_SUBSCRIPTION_ACCESS_TOKEN</code></span>
                      : <span className="flex items-center gap-1"><StatusBadge ok={false} /><code className="ml-1 text-[10px] text-zinc-600 font-mono">MERCADOPAGO_SUBSCRIPTION_ACCESS_TOKEN</code></span>
                }
              />
              <KVRow
                label="Webhook secret"
                value={
                  d.mercadopago.subscriptions.webhook_secret_set
                    ? <StatusBadge ok />
                    : d.mercadopago.subscriptions.direct_checkout_links_configured
                      ? <span className="flex items-center gap-1"><StatusBadge ok label="Opcional" /><code className="ml-1 text-[10px] text-zinc-600 font-mono">MERCADOPAGO_SUBSCRIPTION_WEBHOOK_SECRET</code></span>
                      : <span className="flex items-center gap-1"><StatusBadge ok={false} /><code className="ml-1 text-[10px] text-zinc-600 font-mono">MERCADOPAGO_SUBSCRIPTION_WEBHOOK_SECRET</code></span>
                }
              />
              <KVRow
                label="Direct checkout links (env)"
                value={
                  d.mercadopago.subscriptions.direct_checkout_links_configured
                    ? <StatusBadge ok label="Configurados" />
                    : <span className="flex items-center gap-1"><StatusBadge ok={false} /><code className="ml-1 text-[10px] text-zinc-600 font-mono">VITE_MP_CHECKOUT_BASIC_AR + VIP</code></span>
                }
              />
              <KVRow
                label="Modo de cobro activo"
                value={
                  d.mercadopago.subscriptions.access_token_set
                    ? <StatusBadge ok label="API tokens propios" />
                    : d.mercadopago.subscriptions.direct_checkout_links_configured
                      ? <StatusBadge ok label="Direct links env" />
                      : d.mercadopago.subscriptions.using_payments_token_as_fallback
                        ? <StatusBadge ok={false} warning label="Token pagos (fallback)" />
                        : <StatusBadge ok={false} label="Sin configurar" />
                }
              />

              {/* Mensajes contextuales */}
              {d.mercadopago.subscriptions.direct_checkout_links_configured &&
                !d.mercadopago.subscriptions.access_token_set && (
                <p className="mt-2 rounded-md bg-emerald-500/5 border border-emerald-500/20 px-2.5 py-2 text-xs text-emerald-400">
                  ✓ Suscripciones AR resueltas con direct links de env. <code className="font-mono">MERCADOPAGO_SUBSCRIPTION_ACCESS_TOKEN</code> es opcional (sólo necesario si querés gestionar suscripciones via API: cancelar, listar, reportes).
                </p>
              )}
              {!d.mercadopago.subscriptions.direct_checkout_links_configured &&
                d.mercadopago.subscriptions.using_payments_token_as_fallback && (
                <p className="mt-2 rounded-md bg-amber-500/5 border border-amber-500/20 px-2.5 py-2 text-xs text-amber-400">
                  ⚠ Sin direct links ni access token propio para suscripciones. Configurá los direct links en Railway (<code className="font-mono">VITE_MP_CHECKOUT_BASIC_AR</code> y <code className="font-mono">VITE_MP_CHECKOUT_VIP_AR</code>) o un access token propio para evitar usar el token de pagos como fallback.
                </p>
              )}
            </IntegrationCard>

            {/* ── Resend ── */}
            <IntegrationCard
              icon={Mail}
              title="Resend (Email)"
              ok={d.resend.api_reachable && d.resend.api_key_set}
              warning={d.resend.api_key_set && !d.resend.api_reachable}
              error={d.resend.error}
            >
              <KVRow
                label="API key configurada"
                value={
                  d.resend.api_key_set
                    ? <StatusBadge ok />
                    : <span className="flex items-center gap-1"><StatusBadge ok={false} /><code className="ml-1 text-[10px] text-zinc-600 font-mono">RESEND_API_KEY</code></span>
                }
              />
              <KVRow label="API alcanzable" value={<StatusBadge ok={d.resend.api_reachable} warning={!d.resend.api_reachable} />} />
              <KVRow
                label="From email"
                value={
                  d.resend.from_email_set
                    ? <StatusBadge ok />
                    : <span className="flex items-center gap-1"><StatusBadge ok={false} /><code className="ml-1 text-[10px] text-zinc-600 font-mono">RESEND_FROM_EMAIL</code></span>
                }
              />
              <KVRow
                label="Emails de notificación"
                value={
                  d.resend.notification_emails.length > 0
                    ? <StatusBadge ok label={`${d.resend.notification_emails.length} configurado${d.resend.notification_emails.length !== 1 ? "s" : ""}`} />
                    : <StatusBadge ok={false} label="Ninguno" />
                }
              />
              {d.resend.notification_emails.length > 0 && (
                <div className="mt-2 space-y-1">
                  {d.resend.notification_emails.map((email) => (
                    <p key={email} className="truncate font-mono text-[11px] text-zinc-400">{email}</p>
                  ))}
                </div>
              )}
            </IntegrationCard>

            {/* ── Webhook ganancias (income notifications via Resend) ── */}
            <IncomeNotificationsCard getAuthHeaders={getAuthHeaders} />

            {/* ── Clerk + Brand ── */}
            <IntegrationCard
              icon={ShieldCheck}
              title="Auth & Brand"
              ok={d.clerk.secret_key_set && d.clerk.publishable_key_set && !!d.brand.source_key}
            >
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-600">Clerk</p>
              <KVRow label="Secret key" value={<StatusBadge ok={d.clerk.secret_key_set} />} />
              <KVRow label="Publishable key" value={<StatusBadge ok={d.clerk.publishable_key_set} />} />

              <p className="mb-2 mt-3 text-[10px] font-semibold uppercase tracking-widest text-zinc-600">Brand</p>
              <KVRow
                label="Source key"
                value={
                  d.brand.source_key
                    ? <span className="font-mono text-xs text-zinc-300">{d.brand.source_key}</span>
                    : <StatusBadge ok={false} label="No configurado" />
                }
              />
              <KVRow
                label="Site URL"
                value={
                  d.brand.site
                    ? <span className="truncate font-mono text-xs text-zinc-400 max-w-[140px] block">{d.brand.site}</span>
                    : <span className="text-zinc-600 text-xs">—</span>
                }
              />
              <KVRow
                label="Nombre"
                value={
                  d.brand.person_name
                    ? <span className="text-xs text-zinc-300">{d.brand.person_name}</span>
                    : <span className="text-zinc-600 text-xs">—</span>
                }
              />
            </IntegrationCard>

          </div>

          {/* Info note */}
          <Alert className="border-zinc-700/60 bg-zinc-800/30">
            <Globe className="h-4 w-4 text-zinc-400" />
            <AlertTitle className="text-zinc-300 text-sm">Verificación en tiempo real</AlertTitle>
            <AlertDescription className="text-zinc-500 text-xs space-y-1">
              <p>
                Los checks de API (Stripe balance, MercadoPago /users/me, Resend /domains) se ejecutan en cada solicitud con timeout de 6 s.
              </p>
              <p>
                Esta página se actualiza automáticamente cada {POLL_MS / 1000} segundos.
              </p>
              <p>
                El estado <span className="text-amber-400 font-medium">degradado</span> indica que la configuración es funcional pero incompleta (p. ej. webhook secrets faltantes).
                El estado <span className="text-red-400 font-medium">error</span> indica que una integración crítica no está disponible.
              </p>
            </AlertDescription>
          </Alert>

          {/* Env var hint if BRAND_SOURCE_KEY missing */}
          {!d.brand.source_key && (
            <Alert className="border-red-500/30 bg-red-500/5">
              <KeyRound className="h-4 w-4 text-red-400" />
              <AlertTitle className="text-red-400">BRAND_SOURCE_KEY no configurado</AlertTitle>
              <AlertDescription className="text-zinc-400 text-xs">
                Esta variable es requerida para procesar transacciones. Configurarla en Railway → Variables y redeploy.
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}
    </div>
  );
}
